// src/services/admin/adminStudents.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const adminStudentsApi = createApi({
  reducerPath: "adminStudentsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_BACKEND_URL}/api/admin/students/`,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("token");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["AdminStudents", "Students"],
  endpoints: (builder) => ({
    getStudents: builder.query({
      query: ({
        page,
        limit,
        gradYear,
        name,
        email,
        accessOverrideFilter,
        genStatus,
        dasStatus,
        schoolId,
        cityId,
        appType,
      }) =>
        `all?page=${page}&limit=${limit}&gradYear=${gradYear}${
          name ? `&name=${encodeURIComponent(name)}` : ""
        }${email ? `&email=${encodeURIComponent(email)}` : ""}${
          genStatus ? `&genStatus=${encodeURIComponent(genStatus)}` : ""
        }${dasStatus ? `&dasStatus=${encodeURIComponent(dasStatus)}` : ""}${
          schoolId ? `&schoolId=${encodeURIComponent(schoolId)}` : ""
        }${cityId ? `&cityId=${encodeURIComponent(cityId)}` : ""}${
          cityId ? `&city_id=${encodeURIComponent(cityId)}` : ""
        }${appType ? `&appType=${encodeURIComponent(appType)}` : ""}
        ${
          accessOverrideFilter
            ? `&accessOverrideFilter=${encodeURIComponent(
                accessOverrideFilter
              )}`
            : ""
        }`,
      providesTags: (
        result,
        error,
        {
          page,
          limit,
          gradYear,
          name,
          email,
          accessOverrideFilter,
          genStatus,
          dasStatus,
          schoolId,
          cityId,
          appType,
        }
      ) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: "AdminStudents", id })),
              { type: "AdminStudents", id: "PARTIAL-LIST" },
            ]
          : [{ type: "AdminStudents", id: "PARTIAL-LIST" }],
    }),
    searchStudents: builder.query({
      query: ({ name = "", gradYear = "" } = {}) => {
        let url = "/search-students";
        const params = [];
        if (name) params.push(`name=${encodeURIComponent(name)}`);
        if (gradYear) params.push(`grad_year=${encodeURIComponent(gradYear)}`);
        if (params.length > 0) url += `?${params.join("&")}`;
        return url;
      },
      providesTags: ["Students"],
    }),
    searchSelectionStudents: builder.query({
      query: ({ q = "", city_id, limit = 25 } = {}) => {
        const params = new URLSearchParams();
        if (q) params.append("q", q);
        if (city_id) params.append("city_id", city_id);
        if (limit) params.append("limit", limit);

        return `selection-search?${params.toString()}`;
      },
      providesTags: ["Students"],
    }),
    getAllUsers: builder.query({
      query: ({ page, limit, name, email, status, schoolId, appType }) =>
        `all-users?page=${page}&limit=${limit}&${
          name ? `&name=${encodeURIComponent(name)}` : ""
        }${email ? `&email=${encodeURIComponent(email)}` : ""}${
          status ? `&status=${encodeURIComponent(status)}` : ""
        }${schoolId ? `&schoolId=${encodeURIComponent(schoolId)}` : ""}${
          appType ? `&appType=${encodeURIComponent(appType)}` : ""
        }`,
      providesTags: (
        result,
        error,
        { page, limit, name, email, status, schoolId, appType }
      ) =>
        result
          ? [
              ...result.data.map(({ id }) => ({ type: "AdminStudents", id })),
              { type: "AdminStudents", id: "PARTIAL-LIST" },
            ]
          : [{ type: "AdminStudents", id: "PARTIAL-LIST" }],
    }),
    getStudentById: builder.query({
      query: (id) => `/${id}`,
      providesTags: ["AdminStudents"],
    }),
    getStudentAwardsById: builder.query({
      query: (id) => `/awards/${id}`,
      providesTags: ["AdminStudents"],
    }),
    getStudentsByGradYear: builder.query({
      query: ({ grad_year, city_id }) => `/byGradYear/${grad_year}/${city_id}`,
      providesTags: ["Students"],
    }),
    createStudent: builder.mutation({
      query: (newStudent) => ({
        url: "/",
        method: "POST",
        body: newStudent,
      }),
      invalidatesTags: [{ type: "AdminStudents", id: "PARTIAL-LIST" }],
    }),
    updateStudent: builder.mutation({
      query: ({ id, ...updatedFields }) => ({
        url: `/${id}`,
        method: "PUT",
        body: updatedFields,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "AdminStudents", id },
      ],
    }),
    deleteStudent: builder.mutation({
      query: (id) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [{ type: "AdminStudents", id }],
    }),
    toggleApplicationStatus: builder.mutation({
      query: ({ id, newStatus, applicationType }) => ({
        url: `/${id}/toggle-status`,
        method: "PATCH",
        body: {
          application_status: newStatus,
          application_type: applicationType,
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "AdminStudents", id },
      ],
    }),
    toggleMultipleApplicationStatus: builder.mutation({
      query: ({ studentIds, applicationTypes, newStatus }) => ({
        url: `multi-toggle-status`,
        method: "PATCH",
        body: { studentIds, applicationTypes, newStatus },
      }),
      invalidatesTags: ["AdminStudents"],
    }),
    extendApplicationAccess: builder.mutation({
      query: ({ id, applicationType, overrideDate }) => ({
        url: `/${id}/extend-access`,
        method: "PATCH",
        body: {
          application_type: applicationType,
          overrideDate,
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "AdminStudents", id },
      ],
    }),
    clearApplicationAccess: builder.mutation({
      query: ({ id, applicationType }) => ({
        url: `/${id}/clear-access`,
        method: "PATCH",
        body: {
          application_type: applicationType,
        },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "AdminStudents", id },
      ],
    }),
    sendBulkCustomeEmails: builder.mutation({
      query: ({ studentIds, subject, htmlBody }) => ({
        url: `/bulk-custom-emails`,
        method: "POST",
        body: {
          studentIds,
          subject,
          htmlBody,
        },
      }),
    }),
    massUpdateStudents: builder.mutation({
      query: (updates) => ({
        url: "/mass-update",
        method: "POST",
        body: { updates },
      }),
    }),
    sendBulkEmail: builder.mutation({
      query: ({ recipients, subject, message }) => ({
        url: `/send-bulk-email`,
        method: "POST",
        body: {
          recipients, // array of email addresses
          subject,
          message,
        },
      }),
    }),
  }),
});

export const {
  useGetStudentsQuery,
  useSearchStudentsQuery,
  useLazySearchSelectionStudentsQuery,
  useGetStudentsByGradYearQuery,
  useGetStudentAwardsByIdQuery,
  useGetAllUsersQuery,
  useGetStudentByIdQuery,
  useCreateStudentMutation,
  useUpdateStudentMutation,
  useDeleteStudentMutation,
  useToggleApplicationStatusMutation,
  useToggleMultipleApplicationStatusMutation,
  useExtendApplicationAccessMutation,
  useClearApplicationAccessMutation,
  useSendBulkCustomeEmailsMutation,
  useMassUpdateStudentsMutation,
  useSendBulkEmailMutation,
} = adminStudentsApi;

export default adminStudentsApi;
export { adminStudentsApi };
